import React, { useEffect, useState, useContext } from "react";
import DOMPurify from "dompurify";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import {
  addAssessment,
  getAssessmentByCourseId,
  deleteAssessment,
  updateAssessmentById,
  getAssessmentById,
} from "action/assessment";
import { getBatchList } from "action/batch";
import { courseAssessmentList } from "constants/assessment";
import Select, { components } from "react-select";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Input,
  Label,
  CardFooter,
  Badge,
  UncontrolledTooltip,
  Col,
  Row,
  UncontrolledPopover,
  PopoverBody,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import "react-quill/dist/quill.snow.css";
import {
  DEFAULT_RECORDS_PER_PAGE,
  ASSESSMENT_STATUS_OPTIONS,
  ANSWER_TYPES,
  MULTI_CORRECT_NEGATIVE_OPTIONS,
  DURATION_OPTIONS,
} from "components/constant";
import {
  formatDate,
  formatDateTime,
} from "components/CommonComponents/CommonFunctions";
import { useLocation, useNavigate } from "react-router-dom";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import ExistingQuestionModal from "pages/QuestionBank/ExistingQuestionModal";
import DatePicker from "react-datepicker";
import {
  REGEX,
  fixedWidthCustomStyle,
  SELECT_CUSTOM_STYLES,
  PREVIEW_LIMIT,
} from "constants/CommonConstants";
import RightPanelCard from "components/CommonComponents/RightPanelCard";
import { useDispatch, useSelector } from "react-redux";
import { getQuestionBankDifficultyList } from "action/questionBank";
import InfoPopover from "components/CommonComponents/InfoPopover";

const initialFields = {
  assessmentName: {
    value: "",
    originalValue: "",
    error: "",
  },
  assessmentDuration: {
    value: "",
    originalValue: "",
    error: "",
  },
  availableFrom: {
    value: "",
    originalValue: "",
    error: "",
  },
  availableTill: {
    value: "",
    originalValue: "",
    error: "",
  },
  batchIds: {
    value: [],
    originalValue: [],
    error: "",
  },
  passingPercentage: {
    value: "",
    originalValue: "",
    error: "",
  },
  status: {
    value: "DRAFT",
    originalValue: "DRAFT",
    error: "",
  },
  totalMark: {
    value: "",
    originalValue: "",
    error: "",
  },
  // maxAttempts: {
  //   value: "",
  //   originalValue: "",
  //   error: "",
  // },
  assesmentType: {
    value: "ONLINE",
    originalValue: "ONLINE",
    error: "",
  },
};

const assignInitialFields = {
  assessmentName: {
    value: "",
    originalValue: "",
    error: "",
  },
  status: {
    value: "PUBLISHED",
    originalValue: "PUBLISHED",
    error: "",
  },
  assesmentType: {
    value: "ONLINE",
    originalValue: "ONLINE",
    error: "",
  },
};

function Assessment({ courseId, isOffline, isHybrid }) {
  const { notify } = useContext(NotificationContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const rolePath =
    role === "SUPER_ADMIN"
      ? "super-admin"
      : role === "ADMIN"
        ? "admin"
        : role === "CHANNEL_PARTNER"
          ? "channel-partner"
          : "instructor";

  const canPerformCrudOperation =
    role === "SUPER_ADMIN" || role === "ADMIN" || role === "TRAINER";
  const location = useLocation();
  const assessmentLanguage = new URLSearchParams(location.search).get("lang");
  const { fromAssessmentDetail } = location.state || {};
  const [viewMode, setViewMode] = useState("LIST");
  const [isAssignPanelOpen, setIsAssignPanelOpen] = useState(false);
  const [assignAssessmentId, setAssignAssessmentId] = useState(null);
  const [editAssessmentId, setEditAssessmentId] = useState(null);
  const [formData, setFormData] = useState(initialFields);
  const [assignFormData, setAssignFormData] = useState(assignInitialFields);
  const [pageNum, setPageNum] = useState(1);
  const [searchState, setSearchState] = useState({
    searchName: "",
    assessmentDate: [null, null],
    searchBatch: "",
    status: null,
    filterParams: [],
  });
  const [qbQuestions, setQbQuestions] = useState([]);
  const [originalQbQuestions, setOriginalQbQuestions] = useState([]);
  const [showExistingModal, setShowExistingModal] = useState(false);
  const [previewData, setPreviewData] = useState({ src: "", width: 0, height: 0 });
  const [loader, setLoader] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [expandedOptions, setExpandedOptions] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const assessmentListData = useSelector(
    (state) => state.assessment.courseAssessmentList,
  );

  const questionBankDifficultyData = useSelector(
    (state) => state.questionBanks?.questionDifficultyList,
  );

  const difficultyLevels =
    questionBankDifficultyData?.data?.QuestionDifficultyLevel?.map((item) => ({
      value: item.levelName,
      label: item.levelName
        ? item.levelName.charAt(0).toUpperCase() +
          item.levelName.slice(1).toLowerCase()
        : "",
    })) || [];

  const batchListData = useSelector((state) => state.batch.batchList);

  const batchOptions =
    batchListData?.data?.Batches?.map((batch) => ({
      value: batch.ID,
      label: batch.name,
    })) || [];

  useEffect(() => {
    if (showExistingModal) {
      getQuestionBankDifficultyList(dispatch);
    }
  }, [dispatch, showExistingModal]);

  const handleImagePreview = (e) => {
    const link = e.target.closest("a");
    if (link) {
      e.preventDefault();
    }

    const img = e.target.closest("img");
    if (!img) return;

    e.preventDefault();
    e.stopPropagation();
    setPreviewData({
      src: img.src,
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
  };

  useEffect(() => {
    if (courseId && viewMode !== "LIST") {
      const filterParams = [
        {
          field: "CourseID",
          condition: "eq",
          filterValues: [String(courseId)],
        },
      ];
      getBatchList(dispatch,null, JSON.stringify(filterParams));
    }
  }, [dispatch, courseId, viewMode]);

  useEffect(() => {
    getAssessmentByCourseId(
      dispatch,
      courseId,
      pageNum,
      searchState.filterParams,
    );

    return () => {
      dispatch({ type: courseAssessmentList, payload: {} });
    };
  }, [dispatch, courseId, pageNum, searchState.filterParams]);

  useEffect(() => {
    if (assessmentListData?.code === 200) {
      setLoader({
        isLoading: false,
        error: "",
        noRecords:
          (Array.isArray(assessmentListData?.data)
            ? assessmentListData.data
            : assessmentListData?.data?.assessments || []
          ).length === 0,
        showLoaderOnClick: false,
      });
    } else if (assessmentListData?.code || assessmentListData?.message) {
      setLoader({
        isLoading: false,
        error:
          assessmentListData?.message ||
          "Failed to load assessment list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [assessmentListData]);

  const getPlainText = (html) => {
    if (!html) return "";
    const el = document.createElement("div");
    el.innerHTML = html;
    return el.textContent || "";
  };

  const truncateHtml = (html, limit) => {
    if (!html) return "";
    const container = document.createElement("div");
    container.innerHTML = html;
    let count = 0;
    const walk = (node) => {
      if (count >= limit) {
        node.parentNode?.removeChild(node);
        return;
      }
      if (node.nodeType === Node.TEXT_NODE) {
        const remaining = limit - count;
        if (node.textContent.length > remaining) {
          node.textContent = node.textContent.slice(0, remaining) + "...";
          count = limit;
        } else {
          count += node.textContent.length;
        }
      } else {
        Array.from(node.childNodes).forEach(walk);
      }
    };
    Array.from(container.childNodes).forEach(walk);
    return container.innerHTML;
  };

  const assessments = Array.isArray(assessmentListData?.data)
    ? assessmentListData.data
    : assessmentListData?.data?.assessments || [];

  const tableData = assessments.map((item, index) => ({
    ...item,
    id: item.ID || item.id,
    srNo:
      (pageNum - 1) *
        (assessmentListData?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
      index +
      1,
  }));

  const openEditModal = async (assessment) => {
    try {
      setIsEditLoading(true);
      const res = await getAssessmentById(assessment.id);

      if (res?.code === 200 && res?.data) {
        const details = res.data;

        if (assessment.assessmentType === "OFFLINE") {
          setAssignFormData({
            assessmentName: {
              value: details.assessmentName || "",
              originalValue: details.assessmentName || "",
              error: "",
            },
            status: {
              value: details.status || "DRAFT",
              originalValue: details.status || "DRAFT",
              error: "",
            },
            assesmentType: {
              value: details.assesmentType || "ONLINE",
              originalValue: details.assesmentType || "ONLINE",
              error: "",
            },
          });
          setAssignAssessmentId(assessment.id);
          setIsAssignPanelOpen(true);
        } else {
          setFormData({
            assessmentName: {
              value: details.assessmentName || "",
              originalValue: details.assessmentName || "",
              error: "",
            },
            assessmentDuration: {
              value: details.durationMinutes || "",
              originalValue: details.durationMinutes || "",
              error: "",
            },
            availableFrom: {
              value: details.availableFrom || "",
              originalValue: details.availableFrom || "",
              error: "",
            },
            availableTill: {
              value: details.availableTo || "",
              originalValue: details.availableTo || "",
              error: "",
            },
            batchIds: {
              value: details.batchIDs || [],
              originalValue: details.batchIDs || [],
              error: "",
            },
            passingPercentage: {
              value: details.passingPercentage || "",
              originalValue: details.passingPercentage || "",
              error: "",
            },
            totalMark: {
              value: details.totalMarks || "",
              originalValue: details.totalMarks || "",
              error: "",
            },
            // maxAttempts: {
            //   value: details.maxAttempts || "",
            //   originalValue: details.maxAttempts || "",
            //   error: "",
            // },
            status: {
              value: details.status || "DRAFT",
              originalValue: details.status || "DRAFT",
              error: "",
            },
            assesmentType: {
              value: details.assesmentType || "ONLINE",
              originalValue: details.assesmentType || "ONLINE",
              error: "",
            },
          });
          setEditAssessmentId(assessment.id);
          const mappedQuestions = (details.questions || []).map((q) => ({
            ...q,
            type: q.answerType,
            id: q.questionID,
            _key: `${q.questionID}-${Date.now()}-${Math.random()}`,
          }));
          setQbQuestions(mappedQuestions);
          setOriginalQbQuestions(mappedQuestions);
          setViewMode("EDIT");
        }
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to fetch assessment details!";

        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.message ||
          "Something went wrong while fetching assessment details",
      );
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleAssignInputChange = (field, value) => {
    setAssignFormData((prev) => ({
      ...prev,
      [field]: {
        value,
        error: "",
      },
    }));
  };

  const closeAssignPanel = () => {
    setIsAssignPanelOpen(false);
    setAssignAssessmentId(null);
    setAssignFormData({
      assessmentName: {
        value: "",
        error: "",
      },
      status: {
        value: "PUBLISHED",
        error: "",
      },
      assesmentType: {
        value: "ONLINE",
        error: "",
      },
    });
  };

  const handleDeleteAssessment = (assessmentId) => {
    showAlert({
      title: "Are you sure you want to delete this assessment?",
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await deleteAssessment(assessmentId);

          if (res?.code === 200) {
            notify(
              "success",
              res?.message || "Assessment deleted successfully",
            );

            // If current page becomes empty after deletion,
            // move to previous page.
            if (tableData?.length === 1 && pageNum > 1) {
              setPageNum((prev) => prev - 1);
            } else {
              getAssessmentByCourseId(
                dispatch,
                courseId,
                pageNum,
                searchState.filterParams,
              );
            }
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                  .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
                  .join(", ")
              : res?.message || "Failed to delete assessment";

            notify("danger", message);
          }
        } catch (error) {
          notify("danger", error?.message || "Failed to delete assessment");
        }
      },
    });
  };

  const handleAssignSave = async () => {
    if (!validateAssignForm()) return;
    try {
      setIsSaving(true);
      let res;

      if (assignAssessmentId) {
        // Edit assessment
        const payload = {};

        const data = assignFormData;
        // const keyMapping = {};

        Object.keys(data).forEach((key) => {
          if (
            JSON.stringify(data[key].value) !==
            JSON.stringify(data[key].originalValue)
          ) {
            payload[key] = data[key].value;
          }
        });

        if (!Object.keys(payload).length) {
          notify("info", "No changes detected");
          return;
        }

        res = await updateAssessmentById(assignAssessmentId, payload);
      } else {
        // Add assessment
        const payload = {
          courseID: Number(courseId),
          assessmentName: assignFormData.assessmentName.value,
          assesmentType: "OFFLINE",
          status: assignFormData.status.value,
        };

        res = await addAssessment(payload);
      }

      if (res?.code === 200 || res?.message === "Success") {
        notify(
          "success",
          res?.message ||
            `Assessment ${assignAssessmentId ? "updated" : "added"} successfully`,
        );

        closeAssignPanel();

        getAssessmentByCourseId(
          dispatch,
          courseId,
          pageNum,
          searchState.filterParams,
        );
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message ||
            `Failed to ${assignAssessmentId ? "update" : "add"} assessment`;

        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Something went wrong");
    } finally {
      setIsSaving(false);
    }
  };

  const getAssignValidationError = (name, value) => {
    switch (name) {
      case "assessmentName":
        if (!value?.trim()) return "Please enter assessment name";
        return "";

      default:
        return "";
    }
  };

  const handleAssignOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    setAssignFormData((prev) => ({
      ...prev,
      [name]: {
        value: trimmedValue,
        error: getAssignValidationError(name, trimmedValue),
      },
    }));
  };

  const validateAssignForm = () => {
    const updated = { ...assignFormData };

    Object.keys(updated).forEach((key) => {
      updated[key].error = getAssignValidationError(key, updated[key].value);
    });

    setAssignFormData(updated);

    return Object.values(updated).every((item) => !item.error);
  };

  //from course detail tab = assessment -> return to list if from assessmenmt module then that listing return
  const goBackToList = () => {
    if (fromAssessmentDetail) {
      const encodedCourseId = urlEncodedId(courseId);
      navigate(`/${rolePath}/course/${encodedCourseId}/details?tab=assessment`);
      return;
    }
    setViewMode("LIST");
    setFormData(initialFields);
    setQbQuestions([]);
    setOriginalQbQuestions([]);
    setEditAssessmentId(null);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    if (qbQuestions.length > 0) {
      const totalSelectedMarks = qbQuestions.reduce(
        (acc, q) => acc + (Number(q.marks) || 0),
        0,
      );
      if (totalSelectedMarks !== Number(formData.totalMark.value)) {
        notify(
          "danger",
          `Total marks of selected questions (${totalSelectedMarks}) must equal the assessment total marks (${formData.totalMark.value}).`,
        );
        return;
      }
    }

    try {
      setIsSaving(true);
      let res;
      if (viewMode === "EDIT") {
        const payload = {};
        const keyMapping = {
          assessmentDuration: "durationMinutes",
          availableTill: "availableTo",
          batchIds: "batchIDs",
        };

        Object.keys(formData).forEach((key) => {
          if (
            JSON.stringify(formData[key].value) !==
            JSON.stringify(formData[key].originalValue)
          ) {
            const mappedKey = keyMapping[key] || key;

            payload[mappedKey] = [
              "durationMinutes",
              "passingPercentage",
              // "maxAttempts",
            ].includes(mappedKey)
              ? Number(formData[key].value)
              : ["availableFrom", "availableTo"].includes(mappedKey)
                ? formatDateTime(formData[key].value)
                : formData[key].value;
          }
        });

        const currentQuestionIds = qbQuestions.map((q) => q.id);
        const originalQuestionIds = originalQbQuestions.map((q) => q.id);

        if (
          JSON.stringify(currentQuestionIds) !==
          JSON.stringify(originalQuestionIds)
        ) {
          payload.questionIDs = currentQuestionIds;
        }

        if (!Object.keys(payload).length) {
          notify("info", "No changes detected");
          return;
        }

        res = await updateAssessmentById(editAssessmentId, payload);
      } else {
        const payload = {
          courseID: Number(courseId),
          assesmentType: "ONLINE",
          assessmentName: formData.assessmentName.value,
          durationMinutes: Number(formData.assessmentDuration.value),
          availableFrom: formatDateTime(formData.availableFrom.value),
          availableTo: formatDateTime(formData.availableTill.value),
          batchIDs: formData.batchIds.value,
          passingPercentage: Number(formData.passingPercentage.value),
          status: formData.status.value,
          // maxAttempts: Number(formData.maxAttempts.value),
        };

        if (formData.availableTill.value) {
          payload.availableTo = formatDateTime(formData.availableTill.value);
        }

        if (qbQuestions && qbQuestions.length > 0) {
          payload.questionIDs = qbQuestions.map((q) => q.id);
          payload.totalMarks = qbQuestions.reduce(
            (total, q) => total + (Number(q.marks) || 0),
            0,
          );
        }

        res = await addAssessment(payload);
      }

      if (res?.code === 200) {
        notify(
          "success",
          res?.message ||
            `Assessment ${editAssessmentId ? "updated" : "added"} successfully`,
        );
        goBackToList();
        getAssessmentByCourseId(
          dispatch,
          courseId,
          pageNum,
          searchState.filterParams,
        );
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message ||
            `Failed to ${editAssessmentId ? "update" : "add"} assessment`;

        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Something went wrong");
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      let updatedState = {
        ...prev,
        [field]: {
          ...prev[field],
          value,
          error: "",
        },
      };

      return updatedState;
    });
  };

  const validateForm = () => {
    let newSkill = { ...formData };

    Object.keys(newSkill).forEach((key) => {
      newSkill[key] = {
        ...newSkill[key],
        error: getValidationError(key, newSkill[key].value),
      };
    });

    setFormData(newSkill);
    return Object.values(newSkill).every((item) => item.error === "");
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const buildFilterParams = () => {
    const filterParams = [];

    if (searchState.searchName?.trim()) {
      filterParams.push({
        field: "AssessmentName",
        condition: "like",
        filterValues: [searchState.searchName.trim()],
      });
    }

    if (searchState.searchBatch?.trim()) {
      filterParams.push({
        field: "BatchName",
        condition: "like",
        filterValues: [searchState.searchBatch.trim()],
      });
    }

    if (searchState.status?.value) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [searchState.status.value],
      });
    }

    if (searchState.assessmentDate?.[0]) {
      if (searchState.assessmentDate?.[1]) {
        filterParams.push({
          field: "CreatedAt",
          condition: "btw",
          filterValues: [
            formatDate(searchState.assessmentDate[0]),
            formatDate(searchState.assessmentDate[1]),
          ],
        });
      } else {
        filterParams.push({
          field: "CreatedAt",
          condition: "eq",
          filterValues: [formatDate(searchState.assessmentDate[0])],
        });
      }
    }

    return filterParams;
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(searchState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setLoader((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setSearchState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "assessmentName":
        if (!value?.trim()) return "Assessment name is required";
        // if (value.trim().length < 5)
        //   return "Assessment name must be at least 5 characters";
        if (value.trim().length > 100)
          return "Assessment name cannot exceed 100 characters";
        return "";

      case "assessmentDuration":
        if (!value) return "Assessment duration is required";
        if (Number(value) < 1) return "Duration must be at least 1 minute";
        return "";

      case "availableFrom": {
        if (!value) return "Available from date is required";

        const selectedBatches =
          batchListData?.data?.Batches?.filter((b) =>
            formData.batchIds?.value?.includes(b.ID),
          ) || [];

        if (selectedBatches.length === 0) return "";

        const batchStartDates = selectedBatches
          .map((b) => new Date(b.batchStartDate).getTime())
          .filter((date) => !isNaN(date));

        const batchEndDates = selectedBatches
          .map((b) => new Date(b.batchEndDate).getTime())
          .filter((date) => !isNaN(date));

        if (batchStartDates.length === 0 || batchEndDates.length === 0)
          return "";

        const minBatchStartDate = new Date(Math.min(...batchStartDates));
        minBatchStartDate.setHours(0, 0, 0, 0);

        const maxBatchEndDate = new Date(Math.max(...batchEndDates));
        maxBatchEndDate.setHours(23, 59, 59, 999);
        maxBatchEndDate.setDate(maxBatchEndDate.getDate() + 7);

        const valDate = new Date(value);

        if (valDate < minBatchStartDate) {
          return "Assessment start date must be greater than or equal to batch start date";
        }

        if (valDate > maxBatchEndDate) {
          return "Assessment start date cannot be more than 7 days after the batch end date.";
        }

        return "";
      }

      case "availableTill": {
        const selectedBatches =
          batchListData?.data?.Batches?.filter((b) =>
            formData.batchIds?.value?.includes(b.ID),
          ) || [];

        if (selectedBatches.length === 0 || !value) return "";

        const batchEndDates = selectedBatches
          .map((b) => new Date(b.batchEndDate).getTime())
          .filter((date) => !isNaN(date));

        if (batchEndDates.length === 0) return "";

        const maxBatchEndDate = new Date(Math.max(...batchEndDates));
        maxBatchEndDate.setHours(23, 59, 59, 999);
        maxBatchEndDate.setDate(maxBatchEndDate.getDate() + 7);

        if (new Date(value) > maxBatchEndDate) {
          return "Assessment end date cannot be more than 7 days after the batch end date.";
        }

        return "";
      }

      case "batchIds":
        if (!value || value.length === 0)
          return "At least one batch is required";
        return "";

      case "passingPercentage":
        if (!value && value !== 0) return "Passing percentage is required";
        if (Number(value) < 1 || Number(value) > 100)
          return "Passing percentage must be between 1 and 100";
        return "";

      //for total marks from backend validation is not applied yet
      case "totalMark":
        if (!value && value !== 0) return "Total mark is required";
        if (value <= 0) return "Total mark must be greater than zero";
        return "";

      // case "maxAttempts":
      //   if (!value) return "Maximum attempts is required";
      //   if (Number(value) < 1) return "Maximum attempts must be at least 1";
      //   return "";

      default:
        return "";
    }
  };

  const clearSearch = () => {
    setLoader((prev) => ({
      ...prev,
      isLoading: true,
      showLoaderOnClick: false,
    }));

    setSearchState({
      searchName: "",
      assessmentDate: [null, null],
      searchBatch: "",
      status: null,
      filterParams: [],
    });

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const toggleStatus = (assessment) => {
    const { status, id, assessmentName } = assessment;

    const isActive = status === "PUBLISHED";
    const newStatus = isActive ? "DRAFT" : "PUBLISHED";

    showAlert({
      title: `Are you sure you want to ${
        isActive ? "unpublish" : "publish"
      } this assessment?`,
      message: `Assessment "${assessmentName}" will be ${
        isActive ? "moved to draft" : "published"
      }.`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async (closeAlert) => {
        closeAlert();
        try {
          const payload = {
            status: newStatus,
          };
          const res = await updateAssessmentById(id, payload);
          if (res?.code === 200) {
            notify(
              "success",
              res?.message || `Status updated to ${newStatus.toLowerCase()}`,
            );

            getAssessmentByCourseId(
              dispatch,
              courseId,
              pageNum,
              searchState.filterParams,
            );
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                  .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
                  .join(", ")
              : res?.message || "Failed to update status!";

            notify("danger", message);
          }
        } catch (error) {
          notify("danger", error?.message || "Something went wrong!");
        } finally {
        }
      },
    });
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "PUBLISHED" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "PUBLISHED" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const handleDeleteQuestion = (index) => {
    if (viewMode === "EDIT" && qbQuestions.length <= 1) {
      notify("danger", "at least one question must be selected for an online assessment");
      return;
    }

    showAlert({
      title: "Are you sure you want to delete this question?",
      message:
        "Question will be removed from this assessment only and will not be deleted from the question bank.",
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const updatedQuestions = qbQuestions.filter((_, i) => i !== index);
          const questionIDs = updatedQuestions.map((q) => q.id);

          if (editAssessmentId) {
            const res = await updateAssessmentById(editAssessmentId, {
              questionIDs,
            });

            if (res?.code !== 200) {
              const message = Array.isArray(res?.message)
                ? res.message
                    .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
                    .join(", ")
                : res?.message || "Failed to delete question";

              notify("danger", message);
              return;
            }
          }

          setQbQuestions(updatedQuestions);
          setOriginalQbQuestions(updatedQuestions);

          //here as edit assessment is called so success message is shown in custom format
          notify("success", "Question deleted successfully");
        } catch (error) {
          notify("danger", error?.message || "Failed to delete question");
        }
      },
    });
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 80,
      maxWidth: 80,
      centerAlign: true,
    },
    {
      Header: "Assessment Name",
      accessor: "assessmentName",
      minWidth: 200,
      maxWidth: 200,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    ...(!isOffline
      ? [
          {
            Header: "Duration",
            accessor: "durationMinutes",
            minWidth: 100,
            maxWidth: 100,
            Cell: ({ value }) => (value ? `${value} min` : "-"),
          },
          {
            Header: "Batch",
            accessor: "batchNames",
            minWidth: 180,
            maxWidth: 180,
            Cell: ({ row }) => {
              const batchNames = (row.original.batchNames || []).filter(
                (batch) => batch?.trim(),
              );

              const { ID } = row.original;

              if (!batchNames.length) {
                return "-";
              }

              const visibleBatches = batchNames.slice(0, 3);
              const remainingCount = batchNames.length - 3;
              const batchPopoverId = `batch-${ID}`;

              return (
                <div className="text-wrapper">
                  {visibleBatches.map((batch, index) => (
                    <div className="trainer-name" key={index}>
                      <div className="trainer-row">
                        <span className="trainer-bullet">•</span>
                        <span className="trainer-text">{batch}</span>
                      </div>

                      {index === 2 && remainingCount > 0 && (
                        <>
                          <div className="trainer-count">
                            <Badge
                              id={batchPopoverId}
                              color="primary"
                              pill
                              className="cursor-pointer"
                            >
                              + {remainingCount}
                            </Badge>
                          </div>

                          <UncontrolledPopover
                            target={batchPopoverId}
                            trigger="hover"
                            placement="top"
                          >
                            <PopoverBody>
                              <div className="trainer-popover-list">
                                {batchNames.map((batch, batchIndex) => (
                                  <div key={batchIndex}>• {batch}</div>
                                ))}
                              </div>
                            </PopoverBody>
                          </UncontrolledPopover>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              );
            },
          },
          {
            Header: "Passing Percentage",
            accessor: "passingPercentage",
            minWidth: 150,
            maxWidth: 150,
            Cell: ({ value }) => (value ? `${value}%` : "-"),
          },
          {
            Header: "Availability",
            accessor: "availability",
            centerAlign: true,
            minWidth: 190,
            maxWidth: 190,
            Cell: ({ row }) => {
              const { availableFrom, availableTo } = row.original;

              if (!availableFrom) return "-";

              if (!availableTo) {
                return (
                  <div className="text-wrapper">
                    <div>{availableFrom}</div>
                  </div>
                );
              }

              return (
                <div className="text-wrapper">
                  <div>{availableFrom}</div>
                  <div className="font-weight-bold">to</div>
                  <div>{availableTo}</div>
                </div>
              );
            },
          },
          // {
          //   Header: "Max Question",
          //   accessor: "maxAttempts",
          //   minWidth: 120,
          //   maxWidth: 120,
          //   Cell: ({ value }) => value || "-",
          // },
        ]
      : []),
    {
      Header: "Status",
      accessor: "status",
      centerAlign: true,
      minWidth: 100,
      maxWidth: 100,
      Cell: ({ value }) => (
        <Badge color={value === "PUBLISHED" ? "success" : "warning"} pill>
          {value}
        </Badge>
      ),
    },
    {
      Header: "Created On",
      accessor: "createdAt",
      minWidth: 190,
      maxWidth: 190,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    ...(role !== "CHANNEL_PARTNER"
      ? [
          {
            Header: "Actions",
            accessor: "actions",
            Cell: ({ row }) => {
              const assessment = row.original;
              const { status } = assessment;
              const encodedId = urlEncodedId(assessment.id);
              const isActive = status === "PUBLISHED";

              return (
                <div className="d-flex align-items-center">
                  {/* Edit */}
                  {canPerformCrudOperation && (
                    <>
                      <Button
                        color="link"
                        className="p-0"
                        id={`edit${encodedId}`}
                        onClick={() => openEditModal(assessment)}
                      >
                        <i className="fas fa-edit m-0 ml-1" />
                      </Button>
                      <UncontrolledTooltip
                        delay={0}
                        target={`edit${encodedId}`}
                      >
                        Edit
                      </UncontrolledTooltip>
                    </>
                  )}

                  {/* Divider */}
                  {canPerformCrudOperation && (
                    <span className="mx-1 text-muted">|</span>
                  )}
                  {canPerformCrudOperation && (
                    <>
                      <Button
                        color="link"
                        size="lg"
                        className="d-flex align-items-center p-0 ml-1"
                        id={`status${encodedId}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStatus(assessment);
                        }}
                      >
                        <i
                          className={`fas status-toggle-icon ${
                            isActive
                              ? "fa-toggle-on text-success"
                              : "fa-toggle-off text-danger"
                          }`}
                        />
                      </Button>

                      <UncontrolledTooltip
                        delay={0}
                        target={`status${encodedId}`}
                      >
                        {isActive ? "Draft" : "Published"}
                      </UncontrolledTooltip>
                    </>
                  )}

                  {/* Delete */}
                  {canPerformCrudOperation && assessment.canDelete && (
                    <>
                      <Button
                        color="link"
                        className="btn-link d-flex align-items-center p-0 table-action-delete"
                        id={`delete${encodedId}`}
                        onClick={() => handleDeleteAssessment(assessment.id)}
                      >
                        <i className="fas fa-trash mx-1 text-danger" />
                      </Button>
                      <UncontrolledTooltip
                        delay={0}
                        target={`delete${encodedId}`}
                      >
                        Delete
                      </UncontrolledTooltip>
                    </>
                  )}
                </div>
              );
            },
          },
        ]
      : []),
  ];

  //with value we have to limit to length
  const handleNumberInput = (field, maxDigits) => (e) => {
    const value = e.target.value;

    if (REGEX.ONLY_DIGITS_REGEX.test(value) && value.length <= maxDigits) {
      handleInputChange(field, value);
    }
  };

  return (
    <>
      {viewMode === "LIST" && (
        <Row>
          <Col md="12">
            <Card>
              {isEditLoading ? (
                <CardBody>
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                </CardBody>
              ) : (
                <>
                  <CardHeader className="border-bottom-0">
                    {/* First row - Assign Assessment */}
                    {canPerformCrudOperation && (
                      <div className="d-flex justify-content-end mb-3">
                        <Button
                          className="btn-deep-blue"
                          onClick={() => {
                            if (isOffline) {
                              setAssignFormData({
                                assessmentName: {
                                  value: "",
                                  error: "",
                                },
                                status: {
                                  value: "PUBLISHED",
                                  error: "",
                                },
                                assesmentType: {
                                  value: "OFFLINE",
                                  error: "",
                                },
                              });
                              setIsAssignPanelOpen(true);
                            } else {
                              setViewMode("ADD");
                              setFormData(initialFields);
                              setQbQuestions([]);
                              setOriginalQbQuestions([]);
                            }
                          }}
                        >
                          <i className="fa fa-plus mr-1" />
                          Assessment
                        </Button>
                      </div>
                    )}

                    {/* Second row - Filters */}
                    <div className="d-flex flex-wrap align-items-center filter-group">
                      <Input
                        type="text"
                        placeholder="Search by assessment name"
                        className="mr-2 mb-2 filter-input-large"
                        value={searchState.searchName}
                        onChange={(e) =>
                          setSearchState((prev) => ({
                            ...prev,
                            searchName: e.target.value,
                          }))
                        }
                        onKeyDown={handleKeyDown}
                      />

                      <Input
                        type="text"
                        placeholder="Search by batch name"
                        className="mr-2 mb-2 filter-input-large"
                        value={searchState.searchBatch}
                        onChange={(e) =>
                          setSearchState((prev) => ({
                            ...prev,
                            searchBatch: e.target.value,
                          }))
                        }
                        onKeyDown={handleKeyDown}
                      />

                      <DatePicker
                        selected={searchState.assessmentDate[0]}
                        onChange={(dates) =>
                          setSearchState((prev) => ({
                            ...prev,
                            assessmentDate: dates,
                          }))
                        }
                        startDate={searchState.assessmentDate[0]}
                        endDate={searchState.assessmentDate[1]}
                        selectsRange
                        onKeyDown={(e) => handleKeyDown(e, true)}
                        isClearable={!!searchState.assessmentDate[0]}
                        placeholderText="Select created on date or range"
                        className="form-control mr-2 mb-2 filter-input-large"
                        dateFormat="dd-MMM-yyyy"
                        showMonthDropdown
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={100}
                        calendarClassName="custom-calendar"
                        popperClassName="datepicker-popper"
                        onPaste={(e) => e.preventDefault()}
                      />

                      <Select
                        className="mr-2 mb-2 filter-select"
                        options={ASSESSMENT_STATUS_OPTIONS}
                        placeholder="Select status"
                        components={{ Option, SingleValue }}
                        classNamePrefix="react-select"
                        styles={fixedWidthCustomStyle()}
                        value={searchState.status}
                        onChange={(selected) =>
                          setSearchState((prev) => ({
                            ...prev,
                            status: selected,
                          }))
                        }
                        onKeyDown={handleKeyDown}
                      />

                      <Button
                        color="default"
                        onClick={handleSearch}
                        className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${
                          loader.showLoaderOnClick ? "search-btn" : ""
                        }`}
                      >
                        {loader.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin loader" />
                        ) : (
                          <>
                            <i className="fa fa-search mr-1" />
                            Search
                          </>
                        )}
                      </Button>

                      {searchState.filterParams.length > 0 && (
                        <Button
                          color="default"
                          className="mb-2 filter-btn btn-simple btn-deep-blue"
                          onClick={clearSearch}
                        >
                          <i className="fa fa-times mr-1" />
                          Clear
                        </Button>
                      )}
                    </div>
                  </CardHeader>

                  <CardBody>
                    <div className="mt-n4 mb-3 mx-1">
                      {assessmentListData?.data?.totalRecords > 0 ? (
                        <>
                          {searchState.filterParams.length > 0 ? (
                            <span className="total-records">
                              Showing{" "}
                              {assessmentListData?.data?.totalFilteredRecords}{" "}
                              of {assessmentListData?.data?.totalRecords}{" "}
                              Records
                            </span>
                          ) : (
                            <span className="total-records">
                              Total Records :{" "}
                              {assessmentListData?.data?.totalRecords}
                            </span>
                          )}
                        </>
                      ) : null}
                    </div>

                    <div className="table-responsive">
                      {loader.isLoading ? (
                        <div className="loaderContainer-sm">
                          <i className="fa fa-spinner fa-spin fa-2xl loader" />
                        </div>
                      ) : loader.error ? (
                        <div className="loaderContainer-sm error-style">
                          <div className="error-icon">
                            <i className="fa-solid fa-circle-exclamation"></i>
                          </div>
                          <span>{loader.error}</span>
                        </div>
                      ) : loader.noRecords ? (
                        <div className="loaderContainer-sm empty-style">
                          <div className="empty-icon">
                            <i className="fa-solid fa-circle-info"></i>
                          </div>
                          <span>No assessments found.</span>
                        </div>
                      ) : (
                        <CommonTable
                          columns={columns}
                          data={tableData}
                          defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                          activePage={pageNum}
                          setPageNum={setPageNum}
                          totalPages={Math.ceil(
                            (assessmentListData?.data?.totalFilteredRecords ||
                              0) /
                              (assessmentListData?.data?.noOfRecordsPerPage ||
                                DEFAULT_RECORDS_PER_PAGE),
                          )}
                          showPaginationBottom
                        />
                      )}
                    </div>
                  </CardBody>
                </>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {!isOffline && viewMode !== "LIST" && (
        <>
          <Card>
            <CardHeader className="d-flex align-items-center">
              <i
                className="fa-solid fa-angle-left cursor mr-2"
                onClick={goBackToList}
              ></i>

              <h4 className="mb-0 text-default section-title">
                {viewMode === "ADD" ? "Add Assessment" : "Edit Assessment"}
              </h4>
            </CardHeader>

            <Form onSubmit={(e) => e.preventDefault()}>
              <CardBody>
                <Row>
                  <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label">
                        Assessment Name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder="Enter assessment name"
                        value={formData.assessmentName.value}
                        onChange={(e) =>
                          handleInputChange("assessmentName", e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur("assessmentName", e.target.value)
                        }
                        className={
                          formData.assessmentName.error ? "is-invalid" : ""
                        }
                        maxLength={100}
                      />

                      {formData.assessmentName.error && (
                        <small className="invalid-feedback d-block">
                          {formData.assessmentName.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label d-block">
                        Assessment Duration (minutes)
                        <span className="text-danger">*</span>
                      </Label>

                      <Select
                        className={
                          formData.assessmentDuration.error ? "is-invalid" : ""
                        }
                        styles={SELECT_CUSTOM_STYLES}
                        options={DURATION_OPTIONS}
                        placeholder="Select duration"
                        classNamePrefix="react-select"
                        value={
                          DURATION_OPTIONS.find(
                            (opt) =>
                              opt.value ===
                              String(formData.assessmentDuration.value),
                          ) || null
                        }
                        onChange={(selected) => {
                          handleInputChange(
                            "assessmentDuration",
                            selected ? selected.value : "",
                          );
                          handleOnBlur(
                            "assessmentDuration",
                            selected ? selected.value : "",
                          );
                        }}
                      />

                      {formData.assessmentDuration.error && (
                        <small className="invalid-feedback d-block">
                          {formData.assessmentDuration.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label d-block">
                        Batches <span className="text-danger">*</span>
                      </Label>
                      <Select
                        isMulti
                        className={formData.batchIds?.error ? "is-invalid" : ""}
                        styles={SELECT_CUSTOM_STYLES}
                        options={batchOptions}
                        placeholder="Select Batches"
                        classNamePrefix="react-select"
                        value={batchOptions.filter((batch) =>
                          formData.batchIds.value.includes(batch.value),
                        )}
                        onChange={(selectedOptions) =>
                          handleInputChange(
                            "batchIds",
                            selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : [],
                          )
                        }
                        onBlur={() =>
                          handleOnBlur("batchIds", formData.batchIds?.value)
                        }
                      />
                      {formData.batchIds?.error && (
                        <small className="invalid-feedback d-block">
                          {formData.batchIds.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label d-block">
                        Available From <span className="text-danger">*</span>
                      </Label>
                      <DatePicker
                        selected={
                          formData.availableFrom.value
                            ? new Date(formData.availableFrom.value)
                            : null
                        }
                        onChange={(date) =>
                          handleInputChange("availableFrom", date)
                        }
                        onBlur={() =>
                          handleOnBlur(
                            "availableFrom",
                            formData.availableFrom.value,
                          )
                        }
                        dateFormat="dd-MMM-yyyy"
                        placeholderText="Select available from date"
                        minDate={new Date()}
                        showIcon
                        showMonthDropdown
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={100}
                        icon={
                          <i className="fa-solid fa-calendar-day fa-md mt-1 mr-4 text-muted" />
                        }
                        toggleCalendarOnIconClick
                        className={`form-control ${
                          formData.availableFrom.error ? "is-invalid" : ""
                        }`}
                        wrapperClassName="w-100"
                        calendarClassName="custom-calendar"
                        popperClassName="datepicker-popper"
                        popperPlacement="bottom-start"
                        onKeyDown={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                      />
                      {formData.availableFrom.error && (
                        <small className="invalid-feedback d-block">
                          {formData.availableFrom.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label d-block">
                        Available Till
                      </Label>
                      <DatePicker
                        selected={
                          formData.availableTill.value
                            ? new Date(formData.availableTill.value)
                            : null
                        }
                        onChange={(date) =>
                          handleInputChange("availableTill", date)
                        }
                        onBlur={() =>
                          handleOnBlur(
                            "availableTill",
                            formData.availableTill.value,
                          )
                        }
                        dateFormat="dd-MMM-yyyy"
                        placeholderText="Select available till date"
                        minDate={formData.availableFrom.value}
                        showIcon
                        showMonthDropdown
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={100}
                        icon={
                          <i className="fa-solid fa-calendar-day fa-md mt-1 mr-4 text-muted" />
                        }
                        toggleCalendarOnIconClick
                        className={`form-control ${
                          formData.availableTill.error ? "is-invalid" : ""
                        }`}
                        wrapperClassName="w-100"
                        calendarClassName="custom-calendar"
                        popperClassName="datepicker-popper"
                        popperPlacement="bottom-start"
                        onKeyDown={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                      />
                      {formData.availableTill.error && (
                        <small className="invalid-feedback d-block">
                          {formData.availableTill.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label d-block">
                        Passing Percentage{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder="Enter passing percentage"
                        value={formData.passingPercentage.value}
                        onChange={handleNumberInput("passingPercentage", 2)}
                        onBlur={(e) =>
                          handleOnBlur("passingPercentage", e.target.value)
                        }
                        className={
                          formData.passingPercentage.error ? "is-invalid" : ""
                        }
                      />
                      {formData.passingPercentage.error && (
                        <small className="invalid-feedback d-block">
                          {formData.passingPercentage.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  {/* <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label">
                        Maximum Attempts <span className="text-danger">*</span>
                      </Label>

                      <Input
                        type="text"
                        placeholder="Enter max attempts"
                        value={formData.maxAttempts.value}
                        onChange={handleNumberInput("maxAttempts", 2)}
                        onBlur={(e) =>
                          handleOnBlur("maxAttempts", e.target.value)
                        }
                        className={
                          formData.maxAttempts.error ? "is-invalid" : ""
                        }
                      />

                      {formData.maxAttempts.error && (
                        <small className="invalid-feedback d-block">
                          {formData.maxAttempts.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col> */}
                </Row>
                <Row>
                  <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label">
                        Total Mark <span className="text-danger">*</span>
                        <InfoPopover
                          targetId="totalMarkInfo"
                          title="Total Mark"
                          content="Total mark is updated automatically when a question is deleted."
                        />
                      </Label>
                      <Input
                        type="text"
                        placeholder="Enter total mark"
                        value={formData.totalMark.value}
                        onChange={handleNumberInput("totalMark", 3)} // 3 digit means 100 or 200 can change
                        onBlur={(e) =>
                          handleOnBlur("totalMark", e.target.value)
                        }
                        className={formData.totalMark.error ? "is-invalid" : ""}
                      />

                      {formData.totalMark.error && (
                        <small className="invalid-feedback d-block">
                          {formData.totalMark.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label d-block">
                        Status <span className="text-danger">*</span>
                      </Label>
                      <div className="radio-rapper mt-3">
                        <FormGroup check inline>
                          <Label check>
                            <Input
                              type="radio"
                              value="PUBLISHED"
                              checked={formData.status.value === "PUBLISHED"}
                              onChange={(e) =>
                                handleInputChange("status", e.target.value)
                              }
                            />
                            Published
                          </Label>
                        </FormGroup>

                        <FormGroup check inline>
                          <Label check>
                            <Input
                              type="radio"
                              value="DRAFT"
                              checked={formData.status.value === "DRAFT"}
                              className="ml-4"
                              onChange={(e) =>
                                handleInputChange("status", e.target.value)
                              }
                            />
                            Draft
                          </Label>
                        </FormGroup>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
                {/* <div className="d-flex justify-content-between align-items-center pb-3"> */}
                {/* <FormGroup className="mb-0">
                    <Label className="form-control-label">
                      Negative Grading
                      <InfoPopover
                        targetId="negativeGradingInfo"
                        title="Negative Grading"
                        content="When enabled, incorrect answers will reduce the total score based on the selected grade percentage."
                      />
                    </Label>

                    <div className="d-flex mt-2">
                      <FormGroup check className="mr-5">
                        <Label check className="cursor">
                          <Input
                            type="radio"
                            name="negativeGrading"
                            checked={negativeGrading === true}
                            onChange={() => setNegativeGrading(true)}
                          />
                          Yes
                        </Label>
                      </FormGroup>

                      <FormGroup check>
                        <Label check className="cursor">
                          <Input
                            type="radio"
                            name="negativeGrading"
                            checked={negativeGrading === false}
                            onChange={() => setNegativeGrading(false)}
                          />
                          No
                        </Label>
                      </FormGroup>
                    </div>
                  </FormGroup> */}
                <hr className="divider" />
                <div className="d-flex align-items-center justify-content-between pb-3">
                  {qbQuestions.length > 0 ? (
                    <span className="total-records">
                      Selected Question Marks:{" "}
                      {qbQuestions.reduce(
                        (acc, q) => acc + (Number(q.marks) || 0),
                        0,
                      )}
                    </span>
                  ) : (
                    <span />
                  )}

                  <Button
                    className="btn-deep-blue"
                    onClick={() => {
                      if (
                        !formData.totalMark.value &&
                        formData.totalMark.value !== 0
                      ) {
                        handleOnBlur(
                          "totalMark",
                          formData.totalMark.value || "",
                        );
                        return;
                      }

                      setShowExistingModal(true);
                    }}
                  >
                    <i className="fa fa-plus mr-1 cursor" />
                    Existing Questions
                  </Button>
                </div>
                {/* </div> */}

                {qbQuestions.length === 0 ? (
                  <div className="loaderContainer-sm empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info"></i>
                    </div>
                    <span>No questions added yet.</span>
                  </div>
                ) : (
                  <div
                    className="questions-list mt-3"
                    onClick={handleImagePreview}
                  >
                    {qbQuestions.map((q, index) => (
                      <Card key={q._key} className="section-header mb-4">
                        <CardBody>
                          {/* Header */}
                          <div className="d-flex justify-content-between align-items-start mb-4 question-header">
                            <div className="d-flex flex-grow-1">
                              <div className="question-number-box">
                                Que {index + 1}.
                              </div>

                              <div className="question-content ml-3">
                                {(() => {
                                  let qText =
                                    typeof q.question === "object"
                                      ? q.question?.en || q.question?.hi || ""
                                      : q.question || "";
                                  const plainText = getPlainText(qText);
                                  const isExpanded = expandedQuestions[index];
                                  const shouldTrim =
                                    plainText.length > PREVIEW_LIMIT;
                                  const displayText =
                                    isExpanded || !shouldTrim
                                      ? qText
                                      : truncateHtml(qText, PREVIEW_LIMIT);

                                  return (
                                    <>
                                      <div
                                        className="question-html-content"
                                        dangerouslySetInnerHTML={{
                                          __html:
                                            DOMPurify.sanitize(displayText),
                                        }}
                                      />
                                      {shouldTrim && (
                                        <div className="mt-1">
                                          <button
                                            className="lesson-read-more-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setExpandedQuestions((prev) => ({
                                                ...prev,
                                                [index]: !prev[index],
                                              }));
                                            }}
                                          >
                                            {isExpanded
                                              ? "Read Less"
                                              : "Read More"}
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </div>

                            <div className="question-meta d-flex align-items-center">
                              <span className="question-type-badge mx-1">
                                {ANSWER_TYPES.find(
                                  (item) => item.value === q.type,
                                )?.label || q.type}
                              </span>

                              <span className="question-mark-badge mx-1">
                                Mark:{" "}
                                {String(
                                  q.marks !== undefined ? q.marks : 0,
                                ).padStart(2, "0")}
                              </span>

                              <span className="question-mark-badge mx-1">
                                Level:{" "}
                                {(() => {
                                  const lvl = q.levelName || q.level;
                                  return difficultyLevels.find(
                                    (item) => item.value === lvl,
                                  )?.label;
                                })()}
                              </span>

                              <div className="ml-2 pl-2 border-left">
                                <Button
                                  color="link"
                                  className="btn-link d-flex align-items-center p-0 table-action-delete"
                                  id={`deleteQuestion${index}`}
                                  onClick={() => handleDeleteQuestion(index)}
                                >
                                  <i className="fas fa-trash mx-1 text-danger" />
                                </Button>
                                <UncontrolledTooltip
                                  target={`deleteQuestion${index}`}
                                >
                                  Delete
                                </UncontrolledTooltip>
                              </div>
                            </div>
                          </div>

                          {/* SINGLE & MULTIPLE CHOICE */}
                          {(q.type === "MCQ" ||
                            q.type === "SINGLE_CHOICE" ||
                            q.type === "MULTI" ||
                            q.type === "MULTIPLE_CHOICE") && (
                            <Row>
                              {q.options &&
                                q.options.map((opt, i) => {
                                  const isCorrect =
                                    q.answer?.includes(i) ||
                                    (opt.grade !== undefined && opt.grade > 0);

                                  return (
                                    <Col md="6" key={i} className="mb-3">
                                      <div
                                        className={`question-option-card${
                                          isCorrect
                                            ? " option-correct"
                                            : " option-disabled"
                                        }`}
                                      >
                                        <div className="d-flex align-items-center w-100">
                                          <div
                                            className={`option-icon mr-3${
                                              isCorrect
                                                ? " text-success"
                                                : " text-muted"
                                            }`}
                                          >
                                            {q.type === "MCQ" ||
                                            q.type === "SINGLE_CHOICE" ? (
                                              <i
                                                className={`fa-${
                                                  isCorrect
                                                    ? "solid fa-circle-check text-success"
                                                    : "regular fa-circle text-muted"
                                                }`}
                                              />
                                            ) : (
                                              <i
                                                className={`fa-${
                                                  isCorrect
                                                    ? "solid fa-square-check text-success"
                                                    : "regular fa-square text-muted"
                                                }`}
                                              />
                                            )}
                                          </div>

                                          <div className="flex-grow-1">
                                            <div className="d-flex flex-column w-100">
                                              {(() => {
                                                const optText = opt.text || "";
                                                const plainOptText =
                                                  getPlainText(optText);
                                                const optKey = `${index}-${i}`;
                                                const isOptExpanded =
                                                  expandedOptions[optKey];
                                                const shouldTrimOpt =
                                                  plainOptText.length >
                                                  PREVIEW_LIMIT;
                                                const displayOptText =
                                                  isOptExpanded ||
                                                  !shouldTrimOpt
                                                    ? optText
                                                    : truncateHtml(
                                                        optText,
                                                        PREVIEW_LIMIT,
                                                      );

                                                return (
                                                  <>
                                                    <div className="d-flex align-items-center w-100">
                                                      <div
                                                        className="question-html-content flex-grow-1"
                                                        dangerouslySetInnerHTML={{
                                                          __html:
                                                            DOMPurify.sanitize(
                                                              displayOptText,
                                                            ),
                                                        }}
                                                      />
                                                      {opt.grade !==
                                                        undefined &&
                                                        opt.grade !== null && (
                                                          <span className="option-grade ml-2">
                                                            (
                                                            {MULTI_CORRECT_NEGATIVE_OPTIONS.find(
                                                              (g) =>
                                                                g.value ===
                                                                opt.grade,
                                                            )?.label ||
                                                              `${opt.grade}%`}
                                                            )
                                                          </span>
                                                        )}
                                                    </div>
                                                    {shouldTrimOpt && (
                                                      <div className="mt-1">
                                                        <button
                                                          className="lesson-read-more-btn"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setExpandedOptions(
                                                              (prev) => ({
                                                                ...prev,
                                                                [optKey]:
                                                                  !prev[optKey],
                                                              }),
                                                            );
                                                          }}
                                                        >
                                                          {isOptExpanded
                                                            ? "Read Less"
                                                            : "Read More"}
                                                        </button>
                                                      </div>
                                                    )}
                                                  </>
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </Col>
                                  );
                                })}
                            </Row>
                          )}

                          {/* TRUE FALSE */}
                          {(q.type === "TF" || q.type === "TRUE_FALSE") && (
                            <Row>
                              {q.options &&
                                q.options.map((opt, i) => {
                                  const isCorrect =
                                    q.answer?.includes(i) ||
                                    q.answer?.[0] === i ||
                                    (opt.grade !== undefined && opt.grade > 0);

                                  return (
                                    <Col md="6" key={i} className="mb-3">
                                      <div
                                        className={`question-option-card${
                                          isCorrect
                                            ? " option-correct"
                                            : " option-disabled"
                                        }`}
                                      >
                                        <div className="d-flex align-items-center">
                                          <div
                                            className={`option-icon mr-3${
                                              isCorrect
                                                ? " text-success"
                                                : " text-muted"
                                            }`}
                                          >
                                            <i
                                              className={`fa-${
                                                isCorrect
                                                  ? "solid fa-circle-check text-success"
                                                  : "regular fa-circle text-muted"
                                              }`}
                                            />
                                          </div>

                                          <div className="flex-grow-1">
                                            <div className="d-flex flex-column w-100">
                                              {(() => {
                                                const optText = opt.text || "";
                                                const plainOptText =
                                                  getPlainText(optText);
                                                const optKey = `${index}-${i}`;
                                                const isOptExpanded =
                                                  expandedOptions[optKey];
                                                const shouldTrimOpt =
                                                  plainOptText.length >
                                                  PREVIEW_LIMIT;
                                                const displayOptText =
                                                  isOptExpanded ||
                                                  !shouldTrimOpt
                                                    ? optText
                                                    : truncateHtml(
                                                        optText,
                                                        PREVIEW_LIMIT,
                                                      );

                                                return (
                                                  <>
                                                    <div
                                                      className="question-html-content flex-grow-1"
                                                      dangerouslySetInnerHTML={{
                                                        __html:
                                                          DOMPurify.sanitize(
                                                            displayOptText,
                                                          ),
                                                      }}
                                                    />
                                                    {shouldTrimOpt && (
                                                      <div className="mt-1">
                                                        <button
                                                          className="lesson-read-more-btn"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setExpandedOptions(
                                                              (prev) => ({
                                                                ...prev,
                                                                [optKey]:
                                                                  !prev[optKey],
                                                              }),
                                                            );
                                                          }}
                                                        >
                                                          {isOptExpanded
                                                            ? "Read Less"
                                                            : "Read More"}
                                                        </button>
                                                      </div>
                                                    )}
                                                  </>
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </Col>
                                  );
                                })}
                            </Row>
                          )}

                          {/* IMAGE CHOICE */}
                          {q.type === "IMAGE_CHOICE" && (
                            <Row className="mt-3">
                              {q.options &&
                                q.options.map((opt, i) => {
                                  const isCorrect =
                                    q.answer?.includes(i) ||
                                    (opt.grade !== undefined && opt.grade > 0);

                                  return (
                                    <Col
                                      lg="3"
                                      md="4"
                                      sm="6"
                                      xs="12"
                                      key={i}
                                      className="mb-3"
                                    >
                                      <div
                                        className={`image-option-card${
                                          isCorrect
                                            ? " option-correct"
                                            : " option-disabled"
                                        }`}
                                      >
                                        {opt.image && (
                                          <div className="image-wrapper mb-2">
                                            <img
                                              src={opt.image}
                                              alt={`Option ${i + 1}`}
                                              className="img-fluid rounded"
                                            />
                                          </div>
                                        )}

                                        <div className="d-flex align-items-center">
                                          <div
                                            className={`option-icon mr-2${
                                              isCorrect
                                                ? " text-success"
                                                : " text-muted"
                                            }`}
                                          >
                                            <i
                                              className={`fa-${
                                                isCorrect
                                                  ? "solid fa-square-check text-success"
                                                  : "regular fa-square text-muted"
                                              }`}
                                            />
                                          </div>

                                          <div className="flex-grow-1">
                                            <div className="d-flex flex-column w-100">
                                              {(() => {
                                                const optText = opt.text || "";
                                                const plainOptText =
                                                  getPlainText(optText);
                                                const optKey = `${index}-${i}`;
                                                const isOptExpanded =
                                                  expandedOptions[optKey];
                                                const shouldTrimOpt =
                                                  plainOptText.length >
                                                  PREVIEW_LIMIT;
                                                const displayOptText =
                                                  isOptExpanded ||
                                                  !shouldTrimOpt
                                                    ? optText
                                                    : truncateHtml(
                                                        optText,
                                                        PREVIEW_LIMIT,
                                                      );

                                                return (
                                                  <>
                                                    <div
                                                      className="question-html-content flex-grow-1"
                                                      dangerouslySetInnerHTML={{
                                                        __html:
                                                          DOMPurify.sanitize(
                                                            displayOptText,
                                                          ),
                                                      }}
                                                    />
                                                    {shouldTrimOpt && (
                                                      <div className="mt-1">
                                                        <button
                                                          className="lesson-read-more-btn"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setExpandedOptions(
                                                              (prev) => ({
                                                                ...prev,
                                                                [optKey]:
                                                                  !prev[optKey],
                                                              }),
                                                            );
                                                          }}
                                                        >
                                                          {isOptExpanded
                                                            ? "Read Less"
                                                            : "Read More"}
                                                        </button>
                                                      </div>
                                                    )}
                                                  </>
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </Col>
                                  );
                                })}
                            </Row>
                          )}
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                )}
              </CardBody>

              <CardFooter className="text-right">
                <Button
                  color="secondary"
                  onClick={goBackToList}
                  className="save-btn-sm"
                >
                  Cancel
                </Button>
                <Button
                  className="btn-deep-blue save-btn-sm"
                  onClick={handleSubmit}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <i className="fa fa-spinner fa-spin text-white" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </CardFooter>
            </Form>
          </Card>
        </>
      )}
      <ExistingQuestionModal
        isOpen={showExistingModal}
        language={assessmentLanguage}
        levelOptions={difficultyLevels}
        alreadyAddedQuestionIds={qbQuestions.map((q) => q.id)}
        toggleModal={() => setShowExistingModal(false)}
        onSave={(data) => {
          if (data && data.length > 0) {
            const questionsWithKeys = data.map((q) => ({
              ...q,
              _key: `${q.questionID || q.id}-${Date.now()}-${Math.random()}`,
            }));

            setQbQuestions((prev) => [...prev, ...questionsWithKeys]);
          }
          setShowExistingModal(false);
        }}
      />
      <RightPanelCard isOpen={isAssignPanelOpen} onClose={closeAssignPanel}>
        <Card>
          <CardHeader tag="div" className="p-3">
            <div className="d-flex align-items-center justify-content-between mt-2">
              <div className="d-flex align-items-center">
                <i
                  className="fa-solid fa-angle-left cursor mr-2"
                  onClick={closeAssignPanel}
                />
                <h4 className="mb-0 text-default">
                  {assignAssessmentId ? "Edit Assessment" : "Add Assessment"}
                </h4>
              </div>

              <i
                className="fa-solid fa-xmark cursor text-muted"
                onClick={closeAssignPanel}
              />
            </div>
          </CardHeader>

          <Form onSubmit={(e) => e.preventDefault()}>
            <CardBody>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>
                      Assessment Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      placeholder="Enter assessment name"
                      value={assignFormData.assessmentName.value}
                      onChange={(e) =>
                        handleAssignInputChange(
                          "assessmentName",
                          e.target.value,
                        )
                      }
                      onBlur={(e) =>
                        handleAssignOnBlur("assessmentName", e.target.value)
                      }
                      className={
                        assignFormData.assessmentName.error ? "is-invalid" : ""
                      }
                      maxLength={100}
                    />
                    {assignFormData.assessmentName.error && (
                      <small className="invalid-feedback d-block">
                        {assignFormData.assessmentName.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label>
                      Status
                      <span className="text-danger"> *</span>
                    </Label>

                    <div className="radio-rapper mt-2">
                      <FormGroup check inline>
                        <Label check>
                          <Input
                            type="radio"
                            value="PUBLISHED"
                            checked={
                              assignFormData.status.value === "PUBLISHED"
                            }
                            onChange={(e) =>
                              handleAssignInputChange("status", e.target.value)
                            }
                          />
                          Published
                        </Label>
                      </FormGroup>

                      <FormGroup check inline>
                        <Label check>
                          <Input
                            type="radio"
                            value="DRAFT"
                            checked={assignFormData.status.value === "DRAFT"}
                            onChange={(e) =>
                              handleAssignInputChange("status", e.target.value)
                            }
                          />
                          Draft
                        </Label>
                      </FormGroup>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>

            <CardFooter className="text-right">
              <Button
                color="secondary"
                className="save-btn-sm"
                onClick={closeAssignPanel}
              >
                Cancel
              </Button>

              <Button
                className="btn-deep-blue save-btn-sm"
                onClick={handleAssignSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <i className="fa fa-spinner fa-spin text-white" />
                ) : (
                  "Save"
                )}
              </Button>
            </CardFooter>
          </Form>
        </Card>
      </RightPanelCard>

      <Modal
        isOpen={!!previewData.src}
        toggle={() => setPreviewData({ src: "", width: 0, height: 0 })}
        size="xl"
        centered
      >
        <ModalHeader
          toggle={() => setPreviewData({ src: "", width: 0, height: 0 })}
        >
          Image Preview
        </ModalHeader>
        <ModalBody className="text-center">
          <img src={previewData.src} alt="Preview" className="preview-image" />
        </ModalBody>
      </Modal>
    </>
  );
}

export default Assessment;
